import React, { useState } from 'react';
import dayjs from 'dayjs';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Form, DatePicker, TimePicker, Badge, Modal, Calendar, Input, Checkbox, Space, Tooltip, message, Select as AntdSelect } from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    InfoCircleOutlined,
    LeftOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import Select from '@/components/ui/Select';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import { ToolbarFilterButton } from '@/components/ui/ToolbarFilterButton';
import { useAttendance } from '../hooks/useAttendance';
import CreateAttendanceModal from '../components/CreateAttendanceModal';
import EditAttendanceModal from '../components/EditAttendanceModal';
import HolidayModal from '../components/HolidayModal';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import {
    TenantToolbarSearchWrap,
    TenantToolbarSearchInput,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    ErrorBlock,
} from '@/pages/inside/tenant/style';

const Attendance = () => {
    const {
        total,
        presentCount,
        absentCount,
        tableData,
        columns,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        canCreate,
        canUpdate,
        canDelete,
        createModalOpen,
        setCreateModalOpen,
        createForm,
        onSubmitCreate,
        onSubmitClear,
        closeCreateModal,
        createAttendancePending,
        employeeOptions,
        attendanceError,
        filterDate,
        setFilterDate,
        filterStatus,
        setFilterStatus,
        filterCheckIn,
        setFilterCheckIn,
        filterCheckOut,
        setFilterCheckOut,
        filterModalOpen,
        setFilterModalOpen,
        activeFilterCount,
        clearFilters,
        checkInOptions,
        checkOutOptions,
        ATTENDANCE_STATUS_OPTIONS,
        showCheckInOutInCreateModal,
        page,
        setPage,
        limit,
        setLimit,
        holidays,
        upcomingEvents,
        createHoliday,
        updateHoliday,
        deleteHoliday,
        createHolidayPending,
        updateHolidayPending,
        deleteHolidayPending,
        calendarYear,
        setCalendarYear,
        // Edit attendance
        editModalOpen,
        editingRecord,
        editForm,
        onSubmitEdit,
        closeEditModal,
        updateAttendancePending,
    } = useAttendance();

    const [holidayModalOpen, setHolidayModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [holidayForm] = Form.useForm();

    const handleDateSelect = (date) => {
        setSelectedDate(date);
        const dateStr = date.format('YYYY-MM-DD');
        const existingHoliday = holidays.find((h) => h.date === dateStr || (h.date && h.date.startsWith(dateStr)));

        if (existingHoliday) {
            holidayForm.setFieldsValue({
                title: existingHoliday.title,
                description: existingHoliday.description,
            });
        } else {
            holidayForm.setFieldsValue({
                title: '',
                description: '',
            });
        }
    };

    const openHolidayModal = () => {
        setHolidayModalOpen(true);
        const today = dayjs();
        setSelectedDate(today);
        handleDateSelect(today);
    };

    const onSubmitHoliday = async () => {
        try {
            const values = await holidayForm.validateFields();
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const existingHoliday = holidays.find((h) => h.date === dateStr || (h.date && h.date.startsWith(dateStr)));

            if (existingHoliday) {
                await updateHoliday({
                    id: existingHoliday.id,
                    body: {
                        title: values.title,
                        description: values.description,
                        date: dateStr,
                    },
                });
                message.success('Holiday updated successfully.');
            } else {
                await createHoliday({
                    date: dateStr,
                    title: values.title,
                    description: values.description,
                });
                message.success('Holiday created successfully.');
            }
            // Clear holiday section inputs
            holidayForm.setFieldsValue({ title: '', description: '' });
        } catch (err) {
            console.error('Failed to submit holiday:', err);
        }
    };

    const onDeleteHoliday = async () => {
        const dateStr = selectedDate.format('YYYY-MM-DD');
        const existingHoliday = holidays.find((h) => h.date === dateStr || (h.date && h.date.startsWith(dateStr)));
        if (!existingHoliday) return;

        Modal.confirm({
            title: 'Delete Holiday',
            content: `Are you sure you want to remove the holiday on ${dateStr}? This will also delete any attendance records marked as Holiday on this day.`,
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteHoliday(existingHoliday.id);
                    message.success('Holiday deleted successfully.');
                    holidayForm.resetFields();
                } catch (err) {
                    console.error('Failed to delete holiday:', err);
                }
            },
        });
    };

    const cellRender = (current) => {
        const dateStr = current.format('YYYY-MM-DD');
        const existingHoliday = holidays.find((h) => h.date === dateStr || (h.date && h.date.startsWith(dateStr)));

        if (existingHoliday) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
                    <Tooltip title={`Holiday: ${existingHoliday.title}`}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#7543dc' }} />
                    </Tooltip>
                </div>
            );
        }

        return null;
    };

    const headerRender = ({ value, onChange }) => {
        const handlePrevMonth = () => {
            const newValue = value.clone().subtract(1, 'month');
            onChange(newValue);
            handleDateSelect(newValue);
            if (newValue.year() !== calendarYear) {
                setCalendarYear(newValue.year());
            }
        };

        const handleNextMonth = () => {
            const newValue = value.clone().add(1, 'month');
            onChange(newValue);
            handleDateSelect(newValue);
            if (newValue.year() !== calendarYear) {
                setCalendarYear(newValue.year());
            }
        };

        const handleYearChange = (newYear) => {
            const newValue = value.clone().year(newYear);
            onChange(newValue);
            handleDateSelect(newValue);
            setCalendarYear(newYear);
        };

        const currentYear = dayjs().year();
        const yearOptions = [];
        for (let i = currentYear - 5; i <= currentYear + 5; i++) {
            yearOptions.push({ value: i, label: String(i) });
        }

        return (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px 12px' }}>
                <Button
                    $primary={false}
                    icon={<LeftOutlined />}
                    onClick={handlePrevMonth}
                    $padding="4px 8px"
                    $size="12px"
                    style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                />
                <span style={{ fontSize: '15px', fontWeight: 600, color: 'var(--ant-color-text, #000)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>{value.format('MMMM')}</span>
                    <AntdSelect
                        size="small"
                        value={value.year()}
                        onChange={handleYearChange}
                        options={yearOptions}
                        style={{ width: '80px' }}
                    />
                </span>
                <Button
                    $primary={false}
                    icon={<RightOutlined />}
                    onClick={handleNextMonth}
                    $padding="4px 8px"
                    $size="12px"
                    style={{ minWidth: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                />
            </div>
        );
    };

    return (
        <ListBodyLayout
            title="Attendance"
            description="View and record attendance"
            actions={
                canCreate && (
                    <Space size="middle">
                        <Button
                            $primary={false}
                            icon={<CalendarOutlined />}
                            onClick={openHolidayModal}
                            $padding="10px 20px"
                            $size="14px"
                        >
                            Set Holiday
                        </Button>
                        <Button
                            $primary
                            $blackOnLight
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalOpen(true)}
                            $padding="10px 20px"
                            $size="14px"
                        >
                            Record Attendance
                        </Button>
                    </Space>
                )
            }
            kpiCards={[
                {
                    label: 'Total Records',
                    value: total ?? 0,
                    icon: <CalendarOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                    trend: 'all time',
                    trendPositive: true,
                },
                {
                    label: 'Present',
                    value: presentCount ?? 0,
                    icon: <CheckCircleOutlined style={{ color: '#10b981' }} />,
                    trend: 'active status',
                    trendPositive: true,
                },
                {
                    label: 'Absent',
                    value: absentCount ?? 0,
                    icon: <CloseCircleOutlined style={{ color: '#ef4444' }} />,
                    trend: 'not present',
                    trendPositive: false,
                },
                {
                    label: 'Other',
                    value: (total ?? 0) - (presentCount ?? 0) - (absentCount ?? 0),
                    icon: <InfoCircleOutlined style={{ color: '#8b5cf6' }} />,
                    trend: 'half day / leave',
                    trendPositive: true,
                },
            ]}
        >
            {attendanceError && (
                <ErrorBlock>
                    Unable to load attendance. Check that the HR API (GET /api/v1/hr/attendance) is available.
                </ErrorBlock>
            )}

            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="24px"
            >
                <SliderTab
                    items={[
                        { key: STATUS_TAB.ALL, label: 'All' },
                        { key: STATUS_TAB.PRESENT, label: 'Present' },
                        { key: STATUS_TAB.ABSENT, label: 'Absent' },
                        { key: STATUS_TAB.HALF_DAY, label: 'Half day' },
                        { key: STATUS_TAB.LEAVE, label: 'Leave' },
                        { key: STATUS_TAB.HOLIDAY, label: 'Holiday' },
                    ]}
                    activeKey={statusTab}
                    onChange={setStatusTab}
                    fitContent
                />
                <Container $display="flex" $alignItems="center" $gap="8px" style={{ flexShrink: 0 }}>
                    <TenantToolbarSearchWrap>
                        <span className="tenant-toolbar-search-icon" aria-hidden>
                            <SearchOutlined />
                        </span>
                        <TenantToolbarSearchInput
                            type="search"
                            placeholder="Search"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            aria-label="Search attendance by employee name"
                        />
                    </TenantToolbarSearchWrap>
                    <ToolbarFilterButton
                        title="Filters"
                        open={filterModalOpen}
                        onOpenChange={setFilterModalOpen}
                        hasActiveFilters={activeFilterCount > 0}
                        onClearApplied={clearFilters}
                        onApply={() => setFilterModalOpen(false)}
                        onClearDraft={clearFilters}
                    >
                        <Form layout="vertical">
                            <Form.Item label="Date">
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="YYYY-MM-DD"
                                    value={filterDate}
                                    onChange={(val) => setFilterDate(val)}
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item label="Status">
                                <Select
                                    placeholder="All statuses"
                                    options={ATTENDANCE_STATUS_OPTIONS}
                                    value={filterStatus}
                                    onChange={(val) => setFilterStatus(val)}
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item label="Check in">
                                <Select
                                    placeholder="All"
                                    options={checkInOptions}
                                    value={filterCheckIn}
                                    onChange={(val) => setFilterCheckIn(val)}
                                    allowClear
                                />
                            </Form.Item>
                            <Form.Item label="Check out">
                                <Select
                                    placeholder="All"
                                    options={checkOutOptions}
                                    value={filterCheckOut}
                                    onChange={(val) => setFilterCheckOut(val)}
                                    allowClear
                                />
                            </Form.Item>
                        </Form>
                    </ToolbarFilterButton>
                </Container>
            </Container>

            <Table
                title=""
                columns={columns}
                dataSource={tableData}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showSearch={false}
                showFilter={false}
                showMore={false}
                hideToolbar
                pagination
                paginationTotal={total}
                paginationCurrent={page}
                pageSize={limit}
                onPaginationChange={(newPage, newLimit) => {
                    setPage(newPage);
                    setLimit(newLimit);
                }}
                $topMargin={false}
                $radius={12}
            />

            <CreateAttendanceModal
                open={createModalOpen}
                onCancel={closeCreateModal}
                form={createForm}
                onSubmit={onSubmitCreate}
                employeeOptions={employeeOptions}
                statusOptions={ATTENDANCE_STATUS_OPTIONS}
                showCheckInOut={showCheckInOutInCreateModal}
                loading={createAttendancePending}
            />

            <EditAttendanceModal
                open={editModalOpen}
                onCancel={closeEditModal}
                editingRecord={editingRecord}
                form={editForm}
                onSubmit={onSubmitEdit}
                statusOptions={ATTENDANCE_STATUS_OPTIONS}
                loading={updateAttendancePending}
            />

            <HolidayModal
                open={holidayModalOpen}
                onCancel={() => setHolidayModalOpen(false)}
                selectedDate={selectedDate}
                onDateSelect={handleDateSelect}
                cellRender={cellRender}
                headerRender={headerRender}
                holidays={holidays}
                form={holidayForm}
                onSubmit={onSubmitHoliday}
                onDelete={onDeleteHoliday}
                createLoading={createHolidayPending}
                updateLoading={updateHolidayPending}
                deleteLoading={deleteHolidayPending}
            />
        </ListBodyLayout>
    );
};

export default Attendance;
